<template>
  <div class="core-program-questions">
    <div v-if="isLoading" class="flex justify-center items-center h-[200px]">
      <ActivityIndicator />
    </div>

    <div v-else-if="error" class="text-red-500 mb-4">
      {{ error }}
    </div>

    <div v-else class="space-y-8">
      <!-- Goals Section -->
      <div class="section" data-section="goals">
        <h3 class="text-lg font-semibold text-[#00423D] mb-4">Program Description</h3>
        <QuestionField
          v-model="answers.goals"
          :question="questions.goals"
          placeholder="Enter the description of your program..."
          :error-message="errorMessages.goals"
        />
      </div>

      <!-- Strategy Section -->
      <div class="section" data-section="strategy">
        <h3 class="text-lg font-semibold text-[#00423D] mb-4">Strategy</h3>
        <QuestionField
          v-model="answers.strategy"
          :question="questions.strategy"
          placeholder="Enter your program's strategy..."
          :error-message="errorMessages.strategy"
        />
      </div>

      <!-- Societal Challenge Section -->
      <div class="section" data-section="societalChange">
        <h3 class="text-lg font-semibold text-[#00423D] mb-4">Societal Challenge</h3>
        <QuestionField
          v-model="answers.societalChange"
          :question="questions.societalChange"
          placeholder="Enter your program's Societal Challenge..."
          :error-message="errorMessages.societalChange"
        />
      </div>

      <!-- Solution Section -->
      <div class="section" data-section="solution">
        <h3 class="text-lg font-semibold text-[#00423D] mb-4">Solution</h3>
        <QuestionField
          v-model="answers.solution"
          :question="questions.solution"
          placeholder="Enter your program's solution..."
          :error-message="errorMessages.solution"
        />
      </div>

      <!-- Impact Section -->
      <div class="section" data-section="impact">
        <h3 class="text-lg font-semibold text-[#00423D] mb-4">Impact</h3>
        <QuestionField
          v-model="answers.impact"
          :question="questions.impact"
          placeholder="Enter your program's impact..."
          :error-message="errorMessages.impact"
        />
      </div>

      <!-- Beneficiaries Section -->
      <div class="section" data-section="beneficiaries">
        <h3 class="text-lg font-semibold text-[#00423D] mb-4">Beneficiaries</h3>
        <QuestionField
          v-model="answers.beneficiaries"
          :question="questions.beneficiaries"
          placeholder="Enter your program's beneficiaries..."
          :error-message="errorMessages.beneficiaries"
        />
      </div>

      <!-- Benefits Section -->
      <div class="section" data-section="benefits">
        <h3 class="text-lg font-semibold text-[#00423D] mb-4">Benefits</h3>
        <QuestionField
          v-model="answers.benefits"
          :question="questions.benefits"
          placeholder="Enter your program's benefits..."
          :error-message="errorMessages.benefits"
        />
      </div>

      <!-- Uniqueness Section -->
      <div class="section" data-section="uniqueness">
        <h3 class="text-lg font-semibold text-[#00423D] mb-4">Uniqueness</h3>
        <QuestionField
          v-model="answers.uniqueness"
          :question="questions.uniqueness"
          placeholder="Enter your program's uniqueness..."
          :error-message="errorMessages.uniqueness"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed, watch, nextTick, onUnmounted } from 'vue';
import QuestionField from '@/components/QuestionField.vue';
import ActivityIndicator from '@/components/ActivityIndicator.vue';
import { useAuthStore } from '@/stores/auth';
import { useUserProjectsStore } from '@/stores/projects';
import { useRoute } from 'vue-router';
import global from '@/global';
import { useAutoSave } from '@/composables/useAutoSave';

const route = useRoute();
const authStore = useAuthStore();
const projectsStore = useUserProjectsStore();
const isLoading = ref(true);
const error = ref('');

// Define props
const props = defineProps({
  programId: {
    type: [String, Number],
    required: true
  }
});

// Get program ID from route params
const programSlug = computed(() => route.params.programSlug);

// Get program ID from core programs based on slug
const programId = computed(() => {
  // First try to use the prop
  if (props.programId) {
    return props.programId;
  }
  // Fallback to finding from core programs
  const program = projectsStore.corePrograms.find(
    p => p.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === programSlug.value
  );
  return program?.id;
});

// State management
const errorMessages = ref({});
const answers = ref({});
const lastSavedValues = ref({});
const questions = ref({});
const questionIds = ref({});
const categoryIds = ref({});

// Fetch questions for each section
async function fetchQuestions() {
  try {
    const response = await fetch(`${global.API_URL}/questions/core_programs_questions`, {
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.questions || !Array.isArray(data.questions)) {
      throw new Error('Invalid response format: questions array not found');
    }

    // Create a mapping from question_name to the question data
    const questionMap = {};
    data.questions.forEach(question => {
      questionMap[question.question_name] = question;
    });

    // Define the expected question names and their corresponding keys
    const questionMappings = [
      { key: 'goals', questionName: 'finalizing_your_budget_goal' },
      { key: 'strategy', questionName: 'finalizing_your_budget_strategy' },
      { key: 'societalChange', questionName: 'finalizing_your_budget_societal_change' },
      { key: 'solution', questionName: 'finalizing_your_budget_solution' },
      { key: 'impact', questionName: 'finalizing_your_budget_impact' },
      { key: 'beneficiaries', questionName: 'finalizing_your_budget_beneficiaries' },
      { key: 'benefits', questionName: 'finalizing_your_budget_benefits' },
      { key: 'uniqueness', questionName: 'finalizing_your_budget_uniqueness' }
    ];

    // Process each question mapping
    questionMappings.forEach(mapping => {
      const question = questionMap[mapping.questionName];
      if (question) {
        questions.value[mapping.key] = question.title;
        questionIds.value[mapping.key] = question.id;
        categoryIds.value[mapping.key] = question.category_id;
        answers.value[mapping.key] = '';
        lastSavedValues.value[mapping.key] = '';
        errorMessages.value[mapping.key] = '';
      } else {
        console.error(`Question not found: ${mapping.questionName}`);
        errorMessages.value[mapping.key] = `Failed to load ${mapping.key} question. Please try refreshing the page.`;
      }
    });

  } catch (error) {
    console.error('Error fetching questions:', error);
    throw error;
  }
}

// Add retry logic
const retryCount = ref(0);
const MAX_RETRIES = 3;

// Fetch answers for each section
async function fetchAnswers() {
  if (!programId.value) {
    // If no program ID and we haven't exceeded retries, wait and try again
    if (retryCount.value < MAX_RETRIES) {
      retryCount.value++;
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
      return fetchAnswers();
    }
    error.value = 'Program not found. Please try refreshing the page.';
    return;
  }

  try {
    const sections = [
      'goals',
      'strategy',
      'societalChange',
      'solution',
      'impact',
      'beneficiaries',
      'benefits',
      'uniqueness'
    ];

    // Fetch all answers in parallel for better performance
    const answerPromises = sections.map(async (section) => {
      try {
        const res = await fetch(
          `${global.API_URL}/answers/core_program/${programId.value}?question_id=${questionIds.value[section]}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authStore.user.accessToken}`,
            },
          }
        );

        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }

        const data = await res.json();
        // Check if we have a valid answer in the response
        if (data && data.answer && typeof data.answer.answer === 'string') {
          return { section, answer: data.answer.answer };
        } else {
          // Initialize with empty string if no answer exists
          return { section, answer: '' };
        }
      } catch (error) {
        console.error(`Error fetching ${section} answer:`, error);
        errorMessages.value[section] = `Failed to load ${section} answer. You can still create a new one.`;
        // Initialize with empty string on error
        return { section, answer: '' };
      }
    });

    // Wait for all answers to be fetched
    const results = await Promise.all(answerPromises);
    
    // Update all answers at once
    results.forEach(({ section, answer }) => {
      answers.value[section] = answer;
      lastSavedValues.value[section] = answer;
    });
    
    retryCount.value = 0; // Reset retry count on success
  } catch (error) {
    console.error('Error fetching answers:', error);
    error.value = 'Failed to load answers. Please try again.';
  }
}

// Single watcher for programId changes (remove duplicate watchers)
watch(programId, async (newId, oldId) => {
  if (newId && newId !== oldId) {
    retryCount.value = 0; // Reset retry count when program ID changes
    // Only fetch answers if questions are already loaded
    if (Object.keys(questionIds.value).length > 0) {
      await fetchAnswers();
    }
  }
}, { immediate: true });

// Save a single section answer (used by autosave). Returns true if saved.
async function saveSingleAnswer(section) {
  if (!programId.value) {
    error.value = 'Program not found. Please try refreshing the page.';
    return false;
  }

  const questionId = questionIds.value[section];
  const categoryId = categoryIds.value[section];
  if (!questionId) {
    errorMessages.value[section] = `Question ID not found for ${section}`;
    return false;
  }

  try {
    const response = await fetch(`${global.API_URL}/answers/core_program/${programId.value}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        question_id: questionId,
        answer: answers.value[section],
        category_id: categoryId
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    if (data && data.status) {
      lastSavedValues.value[section] = answers.value[section];
      errorMessages.value[section] = '';
      return true;
    } else {
      throw new Error(`Failed to save ${section} answer: ${data?.message || 'Unknown error'}`);
    }
  } catch (error) {
    console.error(`Error autosaving ${section} answer:`, error);
    errorMessages.value[section] = `Failed to auto-save ${section}. Please try again.`;
    return false;
  }
}

// Save all changed answers (used by the auto-save composable)
async function saveChangedAnswers() {
  const sections = [
    'goals',
    'strategy',
    'societalChange',
    'solution',
    'impact',
    'beneficiaries',
    'benefits',
    'uniqueness'
  ];

  const savePromises = [];
  for (const section of sections) {
    if (answers.value[section] !== lastSavedValues.value[section]) {
      savePromises.push(saveSingleAnswer(section));
    }
  }

  await Promise.all(savePromises);
}

// Setup auto-save watcher using composable
const AUTOSAVE_DELAY = 1500;
const { setupAutoSave, isSaving: isAutoSaving, saveSuccessful, lastSavedValue, updateLastSaved } = useAutoSave(
  () => JSON.stringify(answers.value),
  saveChangedAnswers,
  () => true, // bypass extra validation for autosave
  AUTOSAVE_DELAY
);

// Initialize autosave after answers load and clean up on unmount

// Modify saveAnswers function
async function saveAnswers() {
  if (!programId.value) {
    error.value = 'Program not found. Please try refreshing the page.';
    return false;
  }

  try {

    const sections = [
      'goals',
      'strategy',
      'societalChange',
      'solution',
      'impact',
      'beneficiaries',
      'benefits',
      'uniqueness'
    ];

    const savePromises = sections.map(async (section) => {
      if (answers.value[section] !== lastSavedValues.value[section]) {
        try {
          const response = await fetch(`${global.API_URL}/answers/core_program/${programId.value}`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${authStore.user.accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              question_id: questionIds.value[section],
              answer: answers.value[section],
              category_id: categoryIds.value[section]
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const data = await response.json();
          if (data && data.status) {
            lastSavedValues.value[section] = answers.value[section];
            errorMessages.value[section] = ''; // Clear any previous error messages
            return true;
          } else {
            throw new Error(`Failed to save ${section} answer: ${data?.message || 'Unknown error'}`);
          }
        } catch (error) {
          console.error(`Error saving ${section} answer:`, error);
          errorMessages.value[section] = `Failed to save answer. Please try again.`;
          return false;
        }
      }
      return true;
    });

    const results = await Promise.all(savePromises);
    return results.every(result => result === true);
  } catch (error) {
    console.error('Error saving answers:', error);
    throw error;
  }
}

// Add validation function
const validate = () => {
  let isValid = true;
  const sections = [
    'goals',
    'strategy',
    'societalChange',
    'solution',
    'impact',
    'beneficiaries',
    'benefits',
    'uniqueness'
  ];

  sections.forEach(section => {
    // Remove required validation - all fields are now optional
    // Just clear any existing error messages
    errorMessages.value[section] = '';
  });

  return isValid;
};

// Initialize component
onMounted(async () => {
  try {
    isLoading.value = true;
    error.value = '';

    // Ensure core programs are loaded
    if (!projectsStore.corePrograms.length) {
      await projectsStore.fetchCorePrograms();
    }

    // Fetch questions first, then answers (sequential to ensure questionIds are available)
    await fetchQuestions();
    await fetchAnswers();
    // Initialize the composable's lastSavedValue to the current saved answers snapshot
    try {
      updateLastSaved(JSON.stringify(lastSavedValues.value));
    } catch (e) {
      // noop
    }

    // Start the autosave watcher after answers are loaded
    setupAutoSave(() => JSON.stringify(answers.value));
  } catch (error) {
    console.error('Error initializing component:', error);
    error.value = 'Failed to load questions and answers. Please try refreshing the page.';
  } finally {
    isLoading.value = false;
  }
});

// Cleanup function
onUnmounted(() => {
  // No need to clear any timers as the auto-save logic has been removed
});

// Expose methods to parent component
defineExpose({
  saveAnswers,
  validate
});
</script>

<style scoped>
.core-program-questions {
  padding: 1rem;
}

.section {
  margin-bottom: 2rem;
  padding: 1rem;
  border-bottom: 1px solid #e2e2e2;
}

.section:last-child {
  margin-bottom: 0;
}
</style> 